import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import tiffinOrderService from '../services/tiffinOrderService';
import './TiffinTheme.css';

const WHATSAPP_ADMIN = process.env.REACT_APP_WHATSAPP_ADMIN || '12038877422';

const TiffinSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore from sessionStorage if location.state is lost on page refresh
  const rawState = location.state || (() => {
    try { return JSON.parse(sessionStorage.getItem('tiffinSuccessState') || 'null'); } catch { return null; }
  })();
  const paymentResult = rawState?.paymentResult;
  const orderId = rawState?.orderId;
  const pickupTime = paymentResult?.pickupTime || '';
  const pickupAddress = paymentResult?.pickupAddress || 'Yugal Kunj - Radha Krishna Temple and Community Center, 2769 Duluth Hwy, Duluth, GA 30096';
  const pickupPerson = paymentResult?.pickupPerson || '';
  const mealDate = paymentResult?.mealDate || '';
  const formattedMealDate = mealDate
    ? new Date(mealDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const formatPhone = (phone) => {
    let p = (phone || '').replace(/\D/g, '');
    if (p.length === 10) p = '1' + p;
    return p;
  };

  const buildAdminWhatsAppUrl = () => {
    if (!orderDetail?.order) return null;
    const o = orderDetail.order;
    const itemsText = orderDetail.items?.map(i => `  - ${i.itemName} x${i.quantity} ($${i.lineTotal?.toFixed(2)})`).join('\n') || 'N/A';
    const msg = [
      `*New Tiffin Order #${o.tiffinOrder_Id || orderId}*`,
      ``,
      `*Name:* ${o.firstName} ${o.lastName}`,
      o.phone ? `*Phone:* ${o.phone}` : '',
      `*Email:* ${o.email}`,
      ``,
      formattedMealDate ? `*Meal Date:* ${formattedMealDate}` : '',
      pickupTime ? `*Pickup Time:* ${pickupTime}` : '',
      ``,
      `*Items:*`,
      itemsText,
      ``,
      `*Total:* $${o.totalAmount?.toFixed(2)}`,
      `*Payment:* ${paymentResult?.paymentMethod || 'Paid'}`,
      o.notes ? `*Notes:* ${o.notes}` : '',
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WHATSAPP_ADMIN}?text=${encodeURIComponent(msg)}`;
  };

  const buildUserWhatsAppUrl = () => {
    if (!orderDetail?.order) return null;
    const o = orderDetail.order;
    const userPhone = formatPhone(o.phone);
    if (!userPhone) return null;
    const itemsText = orderDetail.items?.map(i => `  - ${i.itemName} x${i.quantity} ($${i.lineTotal?.toFixed(2)})`).join('\n') || 'N/A';
    const msg = [
      `*Your Tiffin Order Receipt*`,
      ``,
      `Order #${o.tiffinOrder_Id || orderId}`,
      ``,
      formattedMealDate ? `*Meal Date:* ${formattedMealDate}` : '',
      pickupTime ? `*Pickup Time:* ${pickupTime}` : '',
      ``,
      `*Items:*`,
      itemsText,
      ``,
      `*Total:* $${o.totalAmount?.toFixed(2)}`,
      `*Payment:* ${paymentResult?.paymentMethod || 'Paid'}`,
      ``,
      `Thank you for your order! 🙏`,
    ].filter(Boolean).join('\n');
    return `https://wa.me/${userPhone}?text=${encodeURIComponent(msg)}`;
  };

  useEffect(() => {
    if (orderId) {
      fetchOrderDetail(orderId);
    } else {
      setLoading(false);
    }
  }, [orderId]);

  const fetchOrderDetail = async (id) => {
    try {
      const data = await tiffinOrderService.getOrderDetail(id);
      setOrderDetail(data);
    } catch (err) {
      // 404 is expected for kitchen orders — silently skip
      if (err?.response?.status !== 404) {
        console.error('Error fetching order detail:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tiffin-success-page min-h-screen" style={{background: 'var(--yk-bg, #fafafa)'}}>
      {/* Header */}
      <section className="relative" style={{background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,245,238,0.9) 100%)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 1px 12px rgba(120,80,40,0.04)'}}>
        <div className="container-custom py-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center tracking-tight" style={{color: '#44403c', letterSpacing: '-0.02em'}}>
            Order Confirmed!
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{background: 'linear-gradient(90deg, transparent 0%, rgba(180,140,80,0.15) 50%, transparent 100%)'}}></div>
      </section>

      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto">

          {/* Success Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{background: 'linear-gradient(135deg, #fdf6ee, #f5e6d0)', border: '1px solid rgba(200,170,110,0.3)', boxShadow: '0 6px 20px rgba(160,130,70,0.1)'}}>
              <svg className="w-10 h-10" style={{color: '#6b4c0a'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{color: '#292524'}}>Thank You for Your Order!</h2>
            <p style={{color: '#78716c'}}>Your tiffin order has been placed and payment confirmed.</p>
          </div>

          {/* Payment Info */}
          {paymentResult && (
            <div className="rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{color: 'var(--yk-text, #44403c)'}}>
                <span>💳</span> Payment Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="mb-1" style={{color: '#a8a29e'}}>Order ID</p>
                  <p className="font-bold" style={{color: '#292524'}}>#{paymentResult.orderId}</p>
                </div>
                <div>
                  <p className="mb-1" style={{color: '#a8a29e'}}>Transaction ID</p>
                  <p className="font-bold" style={{color: '#292524'}}>{paymentResult.transactionId}</p>
                </div>
                <div>
                  <p className="mb-1" style={{color: '#a8a29e'}}>Amount Paid</p>
                  <p className="font-bold text-lg" style={{color: '#92400e'}}>${paymentResult.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="mb-1" style={{color: '#a8a29e'}}>Payment Method</p>
                  <p className="font-bold" style={{color: '#292524'}}>{paymentResult.paymentMethod}</p>
                </div>
              </div>
            </div>
          )}

          {/* Pickup Info Card */}
          {pickupTime && (
            <div className="rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee 0%, #f5e6d0 100%))', border: '1px solid var(--yk-border, rgba(200,170,110,0.25))', boxShadow: '0 4px 16px rgba(160,130,70,0.1)'}}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{color: '#44403c'}}>
                <span>🕐</span> Pickup Details
              </h3>
              <div className="space-y-2 text-sm">
                {formattedMealDate && (
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📅</span>
                    <div>
                      <p className="text-xs" style={{color: '#a8a29e'}}>Meal Date</p>
                      <p className="font-bold text-base" style={{color: '#92400e'}}>{formattedMealDate}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <span className="text-xl">⏰</span>
                  <div>
                    <p className="text-xs" style={{color: '#a8a29e'}}>Pickup Time</p>
                    <p className="font-bold text-base" style={{color: '#92400e'}}>{pickupTime}</p>
                  </div>
                </div>
                {pickupPerson && (
                  <div className="flex items-center gap-3">
                    <span className="text-xl">👤</span>
                    <div>
                      <p className="text-xs" style={{color: '#a8a29e'}}>Pickup Person</p>
                      <p className="font-semibold" style={{color: '#292524'}}>{pickupPerson}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="text-xl">📍</span>
                  <div>
                    <p className="text-xs" style={{color: '#a8a29e'}}>Pickup Location</p>
                    <p className="font-semibold" style={{color: '#292524'}}>{pickupAddress}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Order Details */}
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{borderColor: '#c8a96e'}}></div>
              <p className="mt-3 text-sm" style={{color: '#a8a29e'}}>Loading order details...</p>
            </div>
          )}

          {!loading && orderDetail?.order && (
            <div className="rounded-2xl p-6 mb-6" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6)'}}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{color: 'var(--yk-text, #44403c)'}}>
                <span>🍱</span> Order Items
              </h3>

              {/* Customer info */}
              <div className="rounded-xl p-4 mb-4 text-sm" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee 0%, #f5e6d0 100%))', border: '1px solid var(--yk-border, rgba(200,170,110,0.2))'}}>
                <p className="mb-1" style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Name:</span> {orderDetail.order.firstName} {orderDetail.order.lastName}</p>
                <p className="mb-1" style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Email:</span> {orderDetail.order.email}</p>
                {orderDetail.order.phone && <p className="mb-1" style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Phone:</span> {orderDetail.order.phone}</p>}
                {orderDetail.order.notes && <p className="mb-1" style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Notes:</span> {orderDetail.order.notes}</p>}
                <p className="mb-1" style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Order Date:</span> {new Date(orderDetail.order.createdDate).toLocaleString()}</p>
                <p style={{color: '#57534e'}}><span className="font-semibold" style={{color: '#44403c'}}>Status:</span> <span className="inline-block font-semibold px-2 py-0.5 rounded-full text-xs" style={{background: 'linear-gradient(135deg, #fdf6ee, #f5e6d0)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.3)'}}>{orderDetail.order.paymentStatus}</span></p>
              </div>

              {/* Items */}
              {orderDetail.items && orderDetail.items.length > 0 && (
                <div className="space-y-3 mb-4">
                  {orderDetail.items.map((item) => (
                    <div key={item.tiffinOrderItem_Id} className="flex items-center justify-between text-sm py-2 last:border-0" style={{borderBottom: '1px solid rgba(180,160,130,0.08)'}}>
                      <div className="flex-1">
                        <p className="font-semibold" style={{color: '#292524'}}>{item.itemName}</p>
                        <p style={{color: '#a8a29e'}}>${item.price?.toFixed(2)} × {item.quantity}</p>
                      </div>
                      <p className="font-bold" style={{color: '#292524'}}>${item.lineTotal?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Total */}
              <div className="pt-4" style={{borderTop: '1px solid rgba(180,160,130,0.1)'}}>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold" style={{color: '#44403c'}}>Total Paid</span>
                  <span className="text-xl font-bold" style={{color: '#92400e'}}>${orderDetail.order.totalAmount?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* No order data fallback */}
          {!loading && !orderDetail?.order && !paymentResult && (
            <div className="rounded-2xl p-8 text-center" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04)'}}>
              <div className="text-4xl mb-3">📋</div>
              <p style={{color: '#78716c'}}>No order information available.</p>
            </div>
          )}

          {/* WhatsApp Notification — hidden per request */}
          {false && !loading && orderDetail?.order && (
            <div className="rounded-2xl p-6 mb-6 text-center" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 2px 12px rgba(120,80,40,0.04), inset 0 1px 0 rgba(255,255,255,0.6))'}}>
              <h3 className="text-lg font-bold mb-2 flex items-center justify-center gap-2" style={{color: '#44403c'}}>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send Order via WhatsApp
              </h3>
              <p className="text-sm mb-4" style={{color: '#a8a29e'}}>Notify the kitchen about your order instantly</p>
              <a
                href={buildAdminWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-bold py-3 px-8 rounded-xl transition-all text-sm hover:scale-[1.02] active:scale-[0.98]"
                style={{boxShadow: '0 4px 15px rgba(37,211,102,0.25)'}}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send WhatsApp Notification
              </a>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <button
              onClick={() => navigate('/tiffin/menu')}
              className="tiffin-action-btn flex-1 font-bold py-3 px-6 rounded-xl transition-all text-center hover:scale-[1.02] active:scale-[0.98]"
              style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}
            >
              Order Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="tiffin-action-btn flex-1 font-semibold py-3 px-6 rounded-xl transition-all text-center hover:scale-[1.02] active:scale-[0.98]"
              style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiffinSuccess;
